import './DynamicScopeTables.scss';

import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { getValuesFromDynamicFilterString } from '@sonar-software/sonar-ts-lib';
import { InitiativeScopeTable } from './InitiativeScopeTable';
import { ScopeTypeSubsection } from './ScopeTypeSubsection';
import { When } from 'react-if';

import { DynamicFilter, SonarObjectType, SonarV3SortDirection, SortByOption } from '@sonar-software/sonar-ts-types';
import { FeatureFlag, SchemaFormat } from '@sonar-software/sonar-core';
import React, { useState } from 'react';
import { ScopeCounts, UpdateQueryParams, useFeatureFlag } from '@hooks';
import { ScopeDisplaySelector, ScopeDisplayType } from './ScopeDisplaySelector';
import { TableFilter, TableFilterQueryParams, TableModifier } from '@ui/table-modifier';
import {
	translateFilterQueryParamsToDynamicQueryLanguage,
	translateOrderQueryParamsToDynamicQueryLanguage
} from '@ui/table-modifier/@arch/lib';

const filters: Array<TableFilter> = [
	{
		filterFormat: SchemaFormat.Primary,
		filterId: 'Name',
		filterLabel: COPY_SPECIFIC.NAME
	},
	{
		filterFormat: SchemaFormat.Owner,
		filterId: 'OwnerIds',
		filterLabel: COPY_SPECIFIC.OWNERS
	}
];

const compactFilters = [ filters[ 0 ] ];

const sortOptions: Array<SortByOption> = [
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'Name',
		label: COPY_SPECIFIC.NAME
	}
];

const convertToDql = (queryParams: TableFilterQueryParams) => ({
	filters: translateFilterQueryParamsToDynamicQueryLanguage(queryParams as DynamicFilter),
	ordering: translateOrderQueryParamsToDynamicQueryLanguage(queryParams)
});

const compactSortOptions: Array<SortByOption> = [];

export interface DynamicScopeTablesProps {
	counts: ScopeCounts;
	initiativeId: number;
	queryParams: { filters: string; ordering: string };
	updateParams: UpdateQueryParams<{ filters: string; ordering: string }>;
}

export const DynamicScopeTables: React.FC<DynamicScopeTablesProps> = ({
	counts,
	initiativeId,
	queryParams,
	updateParams
}) => {
	const isDataDictionaryEnabled = useFeatureFlag(FeatureFlag.DataDictionary);
	const [ displayType, setDisplayType ] = useState<ScopeDisplayType>('comfortable');

	const arrangers = isDataDictionaryEnabled ?
		<ScopeDisplaySelector
			onChange={ setDisplayType }
			value={ displayType } /> :
	 null;

	const onlyScopeTypeIsPicklist =
		counts.length === 1 && SonarObjectType.SalesforcePicklistValue === (counts[ 0 ][ 0 ] as SonarObjectType);

	const isCompact = !isDataDictionaryEnabled || displayType === 'compact';
	const selectNamespace = `initiative-${initiativeId};`;

	return (
		<>
			<TableModifier
				className='dynamic-scope-table-filters'
				convertToDynamicQueryLanguage={ convertToDql }
				customArrangers={ arrangers }
				filterDisplayType='inline'
				filters={ isCompact || onlyScopeTypeIsPicklist ? compactFilters : filters }
				filtersString={ queryParams.filters }
				onChange={ updateParams }
				orderString={ queryParams.ordering }
				sonarObjectId={ undefined }
				sonarObjectType={ undefined }
				sortOptions={ isCompact ? compactSortOptions : sortOptions } />
			<When condition={ isCompact }>
				<InitiativeScopeTable
					count={ counts.reduce((prev, next) => prev + next[ 1 ], 0) }
					dynamicNameFilter={ queryParams.filters }
					initiativeId={ initiativeId }
					nameFilter={
						getValuesFromDynamicFilterString(filters[ 0 ].filterId, queryParams.filters)[ 0 ]?.value ?? ''
					}
					selectNamespace={ selectNamespace } />
			</When>
			<When condition={ isDataDictionaryEnabled && displayType === 'comfortable' }>
				{ counts.map(([ type, count ]) =>
					<ScopeTypeSubsection
						count={ count }
						initiativeId={ initiativeId }
						key={ type }
						overrideAutoCollapse={ counts.length === 1 }
						queryParams={ queryParams }
						selectNamespace={ selectNamespace }
						type={ type } />) }
			</When>
		</>
	);
};
